import { DestroyRef, inject, Service, Signal, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { catchError, map, of, Subscription } from 'rxjs';

import { MY_PROFILE_URL, MyProfileService } from '../profile/my-profile.service';

/**
 * The same endpoint as `MY_PROFILE_URL`, and now literally the same constant — the
 * alias is kept because the phase gates import THIS name (S-10/S-11), and because a
 * second string for one URL is exactly how two readings of one endpoint start to
 * differ.
 */
export const CUSTOMER_PROFILE_URL = MY_PROFILE_URL;

/**
 * The ONE thing the front knows about WHO is logged in: the account type.
 *
 * `CustomerSessionService` deliberately holds no identity — it records the HTTP
 * verdict and nothing else, and the login/refresh bodies carry only `{ csrfToken }`.
 * But some panel sections belong to establishments only („Moje abonamenty", S-11),
 * and a menu entry that answers 403 the moment it is clicked is worse than no entry.
 *
 * Scope, stated so nobody mistakes it for something bigger:
 *
 *  · this is PRESENTATION COURTESY, never an access boundary. The backend decides
 *    access and answers 403; hiding the entry when we are unsure costs nothing,
 *    showing an entry that bounces costs the customer a dead end;
 *  · exactly ONE request per PANEL SESSION, not per SPA lifetime (S-11 impl-review
 *    F1): the panel shell calls `ensureLoaded()` when it mounts, and
 *    `CustomerSessionService` calls `reset()` whenever the session ends, so the
 *    cached answer lives exactly as long as the session it describes. A public page
 *    never calls either, so an anonymous visitor pays nothing;
 *  · ONE field is read out of the profile (`customerType`). Whatever else
 *    `/my/profile` carries is somebody else's contract and must not leak into this
 *    signal;
 *  · a failure of ANY class becomes `null`, i.e. „we do not know" → entry hidden.
 *    There is nothing to retry: the next panel session asks again.
 *
 * WHERE THE VALUE COMES FROM (Q-215/A): from `MyProfileService`, not from an own
 * `HttpClient` call. This service used to fetch `/my/profile` itself, which was
 * harmless only for as long as it was the sole reader in the panel shell; once the
 * shell also asked `MyProfileService` for the account name (the initials avatar),
 * entering the panel cost TWO identical requests and broke the invariant above —
 * the debt named in S-10 impl-review F2 („two caches of one response"), measured by
 * `verify:s10-p3` and `verify:s11-p3`. Reading the neighbour's single-flight cache
 * closes it at the source: one request, one parse, one contract.
 *
 * What did NOT change is the scope. This service still answers one question with one
 * field, and it is still presentation courtesy — depending on the full-profile
 * service moves no access decision into the front.
 */
@Service()
export class CustomerProfileService {
  private readonly myProfile = inject(MyProfileService);
  private readonly destroyRef = inject(DestroyRef);

  private readonly type = signal<number | null>(null);

  /** `0` = osoba prywatna, `1` = placówka (`CUSTOMER_TYPE`); `null` = nie wiadomo. */
  readonly customerType: Signal<number | null> = this.type.asReadonly();

  private asked = false;
  private inFlight: Subscription | null = null;

  /**
   * Called by the panel shell (`PanelLayoutComponent`) on construction — the shell
   * is rebuilt on every entry into the panel, while this service is a root
   * singleton, so this is what makes the request per-session rather than per-app.
   * Repeated calls within one session are free: the answer (or the `null` a failure
   * left behind) is kept until `reset()`.
   */
  ensureLoaded(): void {
    if (this.asked) {
      return;
    }

    this.asked = true;
    this.inFlight = this.myProfile
      .load()
      .pipe(
        map((profile) => profile.customerType),
        // No classification by status here, and that is deliberate: 401, 403, 500,
        // a dead network and a profile body the parser rejects all mean the same
        // thing to a menu entry — we do not know the account type, so we do not
        // offer the section. `MyProfileService` rethrows without classifying, which
        // is precisely what lets this one collapse every class into „unknown".
        catchError(() => of(null)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((value) => {
        this.type.set(value);
      });
  }

  /**
   * Called by `CustomerSessionService` after a successful login AND from its
   * `dropSession()` — the same reset-on-account-change pattern as
   * `MyProfileService.reset()` (S-10 f3, both call sites since S-10 impl-review F1).
   * BOTH services are reset by the session service and by nobody else. Its neighbour
   * had a second, non-session caller between S-14 phase 3 and Q-211/A — the confirmed
   * change of a login address, back when that change was believed to leave the session
   * alive; Q-183/A ended both the belief and the exception. No road here reloads the
   * page, so this singleton lives on; without the reset, the NEXT account in the same
   * tab would have its menu decided on the PREVIOUS account's type — and a session
   * that expired into `/logowanie` never passes through a logout button at all.
   *
   * The subscription is dropped so a late answer cannot resurrect the old account
   * after the state was cleared. Since Q-215/A that is an UNSUBSCRIBE, not a cancelled
   * request: the shared cache (`shareReplay({ refCount: false })`) keeps the HTTP call
   * alive for its other readers, and what matters here is that its answer no longer
   * reaches this signal. Emptying the shared cache is `MyProfileService.reset()`'s job,
   * and `CustomerSessionService` calls BOTH, in that order, on the same session end.
   */
  reset(): void {
    this.inFlight?.unsubscribe();
    this.inFlight = null;
    this.asked = false;
    this.type.set(null);
  }
}
